import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import profilePic from '../assets/profile.jpg';

const skills = [
    { category: 'Frontend', items: [{ name: 'React', level: 8 }, { name: 'TypeScript', level: 7 }, { name: 'Angular', level: 7 }] },
    { category: 'Mobile', items: [{ name: 'React Native', level: 8 }, { name: 'Expo', level: 8 }] },
    { category: 'Backend', items: [{ name: 'Laravel', level: 9 }, { name: 'PHP', level: 9 }, { name: 'Node.js', level: 8 }] },
    { category: 'AI APIs', items: [{ name: 'OpenAI API', level: 8 }, { name: 'Claude API & Claude Code', level: 8 }, { name: 'Gemini', level: 7 }, { name: 'AssemblyAI', level: 7 }, { name: 'Deepgram', level: 7 }, { name: 'Google Document AI', level: 9 }] },
    { category: 'Cloud & Infra', items: [{ name: 'Firebase', level: 9 }, { name: 'Google Cloud', level: 7 }, { name: 'EAS / Expo Cloud', level: 7 }] },
    { category: 'Database', items: [{ name: 'MySQL', level: 7 }, { name: 'Firestore', level: 8 }] },
];

const experience = [
    {
        company: 'Founder & AI Engineer',
        role: 'Edifi, AmmoPantry AI, Yap AI, Build With Limits',
        period: '2023 – Present',
        highlights: [
            '<strong>Edifi</strong> — Family Health OS, YC S26 application. Built full-stack POC in 48 hours (8,000 lines TypeScript).',
            '<strong>AmmoPantry AI</strong> — Live on iOS App Store. AI-powered inventory management.',
            '<strong>Yap AI</strong> — Communication coaching app, launching soon.',
            '<strong>Build With Limits</strong> — Systems for health, fitness, and motherhood.',
        ],
    },
    {
        company: 'Lendio',
        role: 'Senior AI & Full Stack Engineer',
        period: 'Mar 2020 – Aug 2026',
        progression: 'Intern → Engineer → Team Lead → Engineering Lead → Manager',
        highlights: [
            'Led engineering on a decisioning engine that facilitated <strong>$28.7M+ in capital</strong>.',
            'Integrated <strong>Google Document AI</strong> for real-time DSCR processing.',
            'Built planning infrastructure, Datadog/Metabase dashboards, and dev workflows.',
        ],
        award: 'Spirit of Lendio Award — peer-voted, top 5 of ~500 employees',
    },
    {
        company: 'SalesRabbit',
        role: 'Software Engineer Intern',
        period: 'Nov 2018 – Apr 2020',
        highlights: [],
    },
];

const RichText = ({ text }) =>
    text.split(/(<strong>.*?<\/strong>)/g).map((part, i) => {
        const match = part.match(/^<strong>(.*?)<\/strong>$/);
        return match ? <strong key={i} className="font-semibold text-ink">{match[1]}</strong> : part;
    });

const SkillBar = ({ name, level }) => (
    <div className="mb-3">
        <div className="mb-1 flex items-center justify-between">
            <span className="text-sm font-medium text-ink">{name}</span>
            <span className="text-xs font-medium text-muted">{level}/10</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-sand">
            <div className="h-full rounded-full bg-terra transition-all" style={{ width: `${level * 10}%` }} />
        </div>
    </div>
);

const AboutPage = () => {
    return (
        <HelmetProvider>
            <div className="flex-1 bg-cream text-ink">
                <Helmet prioritizeSeoTags>
                    <title>About — Amanda Filomena | AI Engineer & Founder</title>
                    <link rel="canonical" href="https://amandafilomena.com/about" />
                    <meta name="description" content="AI Engineer and founder of Edifi, AmmoPantry AI, Yap AI, and Build With Limits. YC S26 applicant. Building systems for ambitious women." />
                    <meta property="og:title" content="About — Amanda Filomena" />
                    <meta property="og:description" content="AI Engineer, founder, and mom building systems that work in real life." />
                    <meta property="og:url" content="https://amandafilomena.com/about" />
                    <meta property="og:type" content="profile" />
                </Helmet>

                <main className="mx-auto max-w-6xl px-6 py-20 md:py-28">

                    {/* Hero */}
                    <div className="mb-20 grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-start opacity-0 animate-fade-up">
                        <div>
                            <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-4">Who I Am</p>
                            <h1 className="text-3xl md:text-4xl text-ink mb-6" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>
                                About Me
                            </h1>
                            <p className="mb-6 text-lg leading-relaxed text-muted">
                                I'm Amanda — AI Engineer, founder, and mom of two. I build products and systems that work in real life, for ambitious women who don't have time to wait for ideal conditions.
                            </p>
                            <p className="mb-6 text-sm leading-relaxed text-muted">
                                I spent 6+ years at Lendio progressing from intern to Engineering Manager, leading teams and shipping production systems that facilitated $28M+ in capital. I earned the Spirit of Lendio award — peer-voted, top 5 of ~500 employees.
                            </p>
                            <p className="text-sm leading-relaxed text-muted">
                                Now I'm building full-time: <strong className="font-medium text-ink">Edifi</strong> (YC S26 application), <strong className="font-medium text-ink">AmmoPantry AI</strong> (live on App Store), <strong className="font-medium text-ink">Yap AI</strong> (launching soon), and <strong className="font-medium text-ink">Build With Limits</strong> — systems for health, fitness, and motherhood.
                            </p>
                            <div className="mt-10 flex flex-wrap gap-4">
                                <a href="https://www.linkedin.com/in/amanda-fc-anderson/" target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 bg-ink px-6 py-3 text-xs font-medium uppercase tracking-wider text-cream hover:bg-terra transition-all">
                                    LinkedIn
                                </a>
                                <a href="https://github.com/amandafc-BYU" target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 border border-sand px-6 py-3 text-xs font-medium uppercase tracking-wider text-ink hover:border-terra hover:text-terra transition-all">
                                    GitHub
                                </a>
                                <a href="https://www.instagram.com/amandafilomena_/" target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 border border-sand px-6 py-3 text-xs font-medium uppercase tracking-wider text-ink hover:border-terra hover:text-terra transition-all">
                                    Instagram
                                </a>
                            </div>
                        </div>
                        <div className="flex justify-center lg:justify-end">
                            <div className="h-80 w-72 overflow-hidden md:h-[450px] md:w-[380px]">
                                <img src={profilePic} alt="Amanda Filomena — AI Engineer and Founder" className="h-full w-full object-cover" width={380} height={450} loading="eager" />
                            </div>
                        </div>
                    </div>

                    {/* Philosophy */}
                    <div className="border-t border-sand pt-16 mb-20">
                        <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-4">What I Believe</p>
                        <h2 className="mb-10 text-2xl md:text-3xl text-ink" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>The Both/And Life</h2>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                            <div className="bg-surface border border-sand p-8">
                                <h3 className="mb-3 text-base text-ink" style={{ fontFamily: "'Cinzel', serif" }}>Ambition + Presence</h3>
                                <p className="text-sm leading-relaxed text-muted">
                                    You don't have to choose between building something meaningful and being present for your kids. I'm building the counter-narrative.
                                </p>
                            </div>
                            <div className="bg-surface border border-sand p-8">
                                <h3 className="mb-3 text-base text-ink" style={{ fontFamily: "'Cinzel', serif" }}>Systems &gt; Willpower</h3>
                                <p className="text-sm leading-relaxed text-muted">
                                    Most advice assumes margin you don't have. I build systems that hold when life doesn't cooperate.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Experience */}
                    <div className="border-t border-sand pt-16 mb-20">
                        <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-4">Background</p>
                        <h2 className="mb-10 text-2xl md:text-3xl text-ink" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>Experience</h2>
                        <div className="flex flex-col gap-10">
                            {experience.map((job, i) => (
                                <div key={i} className="grid grid-cols-1 gap-4 md:grid-cols-[220px_1fr]">
                                    <div>
                                        <p className="text-base font-bold text-ink">{job.company}</p>
                                        <p className="text-sm text-muted mt-1">{job.period}</p>
                                    </div>
                                    <div>
                                        <p className="text-lg font-bold text-ink mb-2">{job.role}</p>
                                        {job.progression && (
                                            <p className="text-sm font-medium text-terra mb-4">{job.progression}</p>
                                        )}
                                        {job.award && (
                                            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-terra/10 px-4 py-2">
                                                <span className="text-terra text-sm">★</span>
                                                <span className="text-sm font-semibold text-terra">{job.award}</span>
                                            </div>
                                        )}
                                        {job.highlights.length > 0 && (
                                            <ul className="flex flex-col gap-3">
                                                {job.highlights.map((h, j) => (
                                                    <li key={j} className="flex gap-3 text-base leading-relaxed text-muted">
                                                        <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-terra" />
                                                        <span><RichText text={h} /></span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Education */}
                    <div className="border-t border-sand pt-16 mb-20">
                        <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-4">Education</p>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_1fr]">
                            <div>
                                <p className="text-base font-bold text-ink">Brigham Young University</p>
                                <p className="text-sm text-muted mt-1">2015 – 2020</p>
                            </div>
                            <div>
                                <p className="text-lg font-bold text-ink">BS, Computer Engineering</p>
                                <p className="text-base text-muted mt-1">Silicon Slopes, Utah</p>
                            </div>
                        </div>
                    </div>

                    {/* How I build */}
                    <div className="border-t border-sand pt-16 mb-20">
                        <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-4">Process</p>
                        <h2 className="mb-4 text-2xl md:text-3xl text-ink" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>How I Build</h2>
                        <p className="mb-10 text-sm leading-relaxed text-muted max-w-2xl">
                            I run a structured AI-assisted development workflow that lets me ship production-quality software at founder speed. The result: 8,000 lines of TypeScript in 48 hours for Edifi's full-stack POC.
                        </p>
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            <div className="bg-surface border border-sand p-8">
                                <p className="mb-3 text-xs font-medium uppercase tracking-[0.15em] text-terra">Planning</p>
                                <h3 className="mb-3 text-base text-ink" style={{ fontFamily: "'Cinzel', serif" }}>ScopeFlow</h3>
                                <p className="text-sm leading-relaxed text-muted">
                                    My custom agent flow: Claude handles system design, data models, API contracts, and user flows.
                                </p>
                                <a href="https://github.com/amandafc-BYU/scopeflow" target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-terra hover:text-terra-dark transition-colors">
                                    View on GitHub →
                                </a>
                            </div>
                            <div className="bg-surface border border-sand p-8">
                                <p className="mb-3 text-xs font-medium uppercase tracking-[0.15em] text-terra">Scaffolding</p>
                                <h3 className="mb-3 text-base text-ink" style={{ fontFamily: "'Cinzel', serif" }}>GStack</h3>
                                <p className="text-sm leading-relaxed text-muted">
                                    A battle-tested base template that scaffolds auth, data layers, navigation, and infra.
                                </p>
                                <a href="https://github.com/garrytan/gstack" target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-terra hover:text-terra-dark transition-colors">
                                    View on GitHub →
                                </a>
                            </div>
                            <div className="bg-surface border border-sand p-8">
                                <p className="mb-3 text-xs font-medium uppercase tracking-[0.15em] text-terra">Implementation</p>
                                <h3 className="mb-3 text-base text-ink" style={{ fontFamily: "'Cinzel', serif" }}>Claude Code</h3>
                                <p className="text-sm leading-relaxed text-muted">
                                    AI handles implementation velocity. Product decisions, technical direction, and architecture are mine.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Technical skills */}
                    <div className="border-t border-sand pt-16">
                        <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-4">Technical</p>
                        <h2 className="mb-10 text-2xl md:text-3xl text-ink" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>Skills</h2>
                        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
                            {skills.map((group) => (
                                <div key={group.category}>
                                    <h3 className="mb-5 text-sm font-bold uppercase tracking-widest text-muted">{group.category}</h3>
                                    {group.items.map((skill) => (
                                        <SkillBar key={skill.name} name={skill.name} level={skill.level} />
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </main>

                <footer className="border-t border-sand px-6 py-8">
                    <div className="mx-auto max-w-6xl flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <p className="text-sm text-muted">© 2026 Amanda Filomena</p>
                        <div className="flex gap-6">
                            <a href="https://github.com/amandafc-BYU" target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-wider text-muted hover:text-terra transition-colors">
                                GitHub
                            </a>
                            <a href="mailto:hello@amandafilomena.com" className="text-xs uppercase tracking-wider text-muted hover:text-terra transition-colors">
                                Contact
                            </a>
                        </div>
                    </div>
                </footer>
            </div>
        </HelmetProvider>
    );
};

export default AboutPage;
